import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { BookingProvider } from './contexts/BookingContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { PWAUtils } from './utils/pwa';
import { initDB } from './utils/offlineStorage';
import { useOfflineBooking } from './hooks/useOfflineBooking';
import { InstallPrompt } from './components/ui/InstallPrompt';
import { OfflineIndicator } from './components/ui/OfflineIndicator';
import SidebarLayout from './components/common/SidebarLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import InventoryProtectedRoute from './components/common/InventoryProtectedRoute';
import { AdminLayout } from './components/admin';

// Lazy load all page components for better initial load performance
const HomePage = lazy(() => import('./pages/HomePage'));
const RoleSelectionPage = lazy(() => import('./pages/RoleSelectionPage'));
const LandingPageAlt = lazy(() => import('./pages/LandingPageAlt'));
const CustomerLandingPage = lazy(() => import('./pages/CustomerLandingPage'));
const BarberLandingPage = lazy(() => import('./pages/BarberLandingPage'));
const BarbershopOwnerLandingPage = lazy(() => import('./pages/BarbershopOwnerLandingPage'));
const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ConfirmEmailPage = lazy(() => import('./pages/ConfirmEmailPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const BarbershopsPage = lazy(() => import('./pages/BarbershopsPage'));
const BarbershopDetailPage = lazy(() => import('./pages/BarbershopDetailPage'));
const BookBarberPage = lazy(() => import('./pages/BookBarberPage'));
const ShopOwnerOnboardingPage = lazy(() => import('./pages/ShopOwnerOnboardingPage'));
const ShopOwnerSubscriptionPage = lazy(() => import('./pages/ShopOwnerSubscriptionPage'));

// Lazy load pages that use Stripe to prevent early initialization
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const BookingsRequiringConfirmation = lazy(() => import('./pages/BookingsRequiringConfirmation'));
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfilePage'));

// Lazy load other heavy pages for better initial load performance
const BarbershopManagementPage = lazy(() => import('./pages/BarbershopManagementPage'));
const BarbershopFormPage = lazy(() => import('./pages/BarbershopFormPage'));
const BarberManagementPage = lazy(() => import('./pages/BarberManagementPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const BarbershopApprovalPage = lazy(() => import('./pages/BarbershopApprovalPage'));
const EnhancedBarbershopApprovalPage = lazy(() => import('./pages/EnhancedBarbershopApprovalPage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const RevenueForecastPage = lazy(() => import('./pages/RevenueForecastPage'));
const NoShowManagementPage = lazy(() => import('./pages/NoShowManagementPage'));
const FavoriteBarbersPage = lazy(() => import('./pages/FavoriteBarbersPage'));
const GiftCardsPage = lazy(() => import('./pages/GiftCardsPage'));
const MyReviewsPage = lazy(() => import('./pages/MyReviewsPage'));
const BarberPerformanceDashboard = lazy(() => import('./pages/BarberPerformanceDashboard'));
const BarberDashboard = lazy(() => import('./pages/BarberDashboard'));
const BookingAnalyticsDashboard = lazy(() => import('./pages/BookingAnalyticsDashboard'));
const UsageReportingDashboard = lazy(() => import('./pages/UsageReportingDashboard'));
const EnhancedCustomerRetentionDashboard = lazy(() => import('./pages/analytics/CustomerRetentionDashboard'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const NoShowPreventionDashboard = lazy(() => import('./pages/admin/NoShowPreventionDashboard'));
const AuditDashboardPage = lazy(() => import('./pages/admin/AuditDashboardPage'));
const SystemHealthPage = lazy(() => import('./pages/admin/SystemHealthPage'));
const AdminInviteCodesPage = lazy(() => import('./pages/admin/AdminInviteCodesPage'));
const ReviewModerationPage = lazy(() => import('./pages/admin/ReviewModerationPage'));
const AdminWebhookEventsPage = lazy(() => import('./pages/admin/AdminWebhookEventsPage'));
const AdminDemoPage = lazy(() => import('./pages/admin/AdminDemoPage'));
const AdminGiftCardsPage = lazy(() => import('./pages/admin/AdminGiftCardsPage'));
const VisualHistoryPage = lazy(() => import('./pages/VisualHistoryPage'));
const GroupBookingsPage = lazy(() => import('./pages/GroupBookingsPage'));
const GroupBookingDetailPage = lazy(() => import('./pages/GroupBookingDetailPage'));
const GroupInvitationPage = lazy(() => import('./pages/GroupInvitationPage'));
const RecurringAppointmentsPage = lazy(() => import('./pages/RecurringAppointmentsPage'));
const UIComponentsTest = lazy(() => import('./pages/UIComponentsTest'));
const BarberAnalyticsPage = lazy(() => import('./pages/BarberAnalyticsPage'));
const BarberPayoutsPage = lazy(() => import('./pages/BarberPayoutsPage'));
const BarberClientsPage = lazy(() => import('./pages/BarberClientsPage'));
const CalendarAvailabilityPage = lazy(() => import('./pages/CalendarAvailabilityPage'));
const BarberProfilePage = lazy(() => import('./pages/BarberProfilePage'));
const BarberShopsManagementPage = lazy(() => import('./pages/BarberShopsManagementPage'));
const BarberServicesPage = lazy(() => import('./pages/BarberServicesPage'));
const AtRiskClientsPage = lazy(() => import('./pages/AtRiskClientsPage'));
const LostClientsPage = lazy(() => import('./pages/LostClientsPage'));
const WinBackOfferTemplatesPage = lazy(() => import('./pages/WinBackOfferTemplatesPage'));
const IssuedOffersPage = lazy(() => import('./pages/IssuedOffersPage'));
const PendingBookingsPage = lazy(() => import('./pages/PendingBookingsPage'));
const CustomerTrustSettingsPage = lazy(() => import('./pages/CustomerTrustSettingsPage'));
const BookingHistoryPage = lazy(() => import('./pages/BookingHistoryPage'));
const BillingHistoryPage = lazy(() => import('./pages/BillingHistoryPage'));

// Advanced Booking Pages
const MultiServiceBookingPage = lazy(() => import('./pages/MultiServiceBookingPage'));
const BookingModificationPage = lazy(() => import('./pages/BookingModificationPage'));
const EnhancedGroupBookingPage = lazy(() => import('./pages/EnhancedGroupBookingPage'));
const EnhancedRecurringBookingPage = lazy(() => import('./pages/EnhancedRecurringBookingPage'));

// Booking Pages
const BookingPage = lazy(() => import('./pages/BookingPage'));

// Barber Onboarding
const BarberOnboardingWizard = lazy(() => import('./components/BarberOnboardingWizard'));

// Subscription System Pages
const BarberSubscriptionPage = lazy(() => import('./pages/BarberSubscriptionPage'));
const BarberMembershipPlansPage = lazy(() => import('./pages/BarberMembershipPlansPage'));
const BarberMembershipPlansManagePage = lazy(() => import('./pages/BarberMembershipPlansManagePage'));
const SubscriptionCheckoutSuccess = lazy(() => import('./pages/SubscriptionCheckoutSuccess'));
const SubscriptionCheckoutCancel = lazy(() => import('./pages/SubscriptionCheckoutCancel'));

// Customer Membership Pages
const CustomerMembershipPlansPage = lazy(() => import('./pages/CustomerMembershipPlansPage'));
const CustomerMembershipDashboardPage = lazy(() => import('./pages/CustomerMembershipDashboardPage'));
const CustomerMembershipSubscribePage = lazy(() => import('./pages/CustomerMembershipSubscribePage'));
const BarberMembershipCheckoutPage = lazy(() => import('./pages/BarberMembershipCheckoutPage'));
const PromoCodeManagementPage = lazy(() => import('./pages/PromoCodeManagementPage'));

// Barber Invitation and Seat Management Pages
const BarberInvitationPage = lazy(() => import('./pages/BarberInvitationPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));

// Inventory Management Page
const InventoryManagementPage = lazy(() => import('./pages/InventoryManagementPage'));

// Check-In QR Code Pages
const CheckInQrCodePage = lazy(() => import('./pages/CheckInQrCodePage'));
const QrScanResultPage = lazy(() => import('./pages/QrScanResultPage'));

// Loyalty Program Page
const BarberLoyaltyProgramPage = lazy(() => import('./pages/BarberLoyaltyProgramPage'));

// Promotions & Campaigns Pages
const BarberPromotionsPage = lazy(() => import('./pages/BarberPromotionsPage'));
const BarberCampaignsPage = lazy(() => import('./pages/BarberCampaignsPage'));

// Notification Pages
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferencesPage'));
const NotificationHistoryPage = lazy(() => import('./pages/NotificationHistoryPage'));
const AdminNotificationDashboard = lazy(() => import('./pages/AdminNotificationDashboard'));
const SmsOptInPage = lazy(() => import('./pages/SmsOptInPage'));

// Messaging Pages
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const BroadcastPage = lazy(() => import('./pages/BroadcastPage'));

// Legal and Company Pages
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

// PWA Components Wrapper - handles offline indicator and install prompt
const PWAComponents: React.FC = () => {
  const { pendingCount, syncPendingBookings } = useOfflineBooking();

  return (
    <>
      <OfflineIndicator
        pendingCount={pendingCount}
        onRetrySync={async () => {
          await syncPendingBookings();
        }}
      />
      <InstallPrompt />
    </>
  );
};

function App() {
  useEffect(() => {
    // Initialize PWA utilities
    PWAUtils.init();

    // Initialize IndexedDB for offline storage
    initDB().catch((error) => {
      console.error('Failed to initialize offline storage:', error);
    });

    // Set up network status listeners
    const handleOnline = () => {
      PWAUtils.showNotification('BarbrIQ', {
        body: 'You are back online!',
        icon: '/logo192.png'
      });
    };

    const handleOffline = () => {
      PWAUtils.showNotification('BarbrIQ', {
        body: 'You are now offline. Some features may be limited.',
        icon: '/logo192.png'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <SubscriptionProvider>
            <BookingProvider>
              <Router>
                <SidebarProvider>
              {/* PWA Components - Offline Indicator & Install Prompt */}
              <PWAComponents />
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>}>
                <Routes>
                  {/* Admin Routes - Use AdminLayout with dedicated admin sidebar */}
                  <Route path="/admin" element={
                    <ProtectedRoute requiredRole="Admin">
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagementPage />} />
                    <Route path="approvals" element={<BarbershopApprovalPage />} />
                    <Route path="approvals/enhanced" element={<EnhancedBarbershopApprovalPage />} />
                    <Route path="notifications" element={<AdminNotificationDashboard />} />
                    <Route path="promo-codes" element={<PromoCodeManagementPage />} />
                    <Route path="campaigns" element={<BarberCampaignsPage />} />
                    <Route path="win-back-offers" element={<WinBackOfferTemplatesPage />} />
                    <Route path="analytics" element={<AnalyticsDashboard />} />
                    <Route path="no-shows" element={<NoShowManagementPage />} />
                    <Route path="no-show-prevention" element={<NoShowPreventionDashboard />} />
                    <Route path="audit" element={<AuditDashboardPage />} />
                    <Route path="support" element={<SupportTicketPage />} />
                    <Route path="system-health" element={<SystemHealthPage />} />
                    <Route path="usage" element={<UsageReportingDashboard />} />
                    <Route path="invite-codes" element={<AdminInviteCodesPage />} />
                    <Route path="reviews" element={<ReviewModerationPage />} />
                    <Route path="webhook-events" element={<AdminWebhookEventsPage />} />
                    <Route path="demo" element={<AdminDemoPage />} />
                    <Route path="gift-cards" element={<AdminGiftCardsPage />} />
                  </Route>

                  {/* Landing pages - standalone without SidebarLayout (have their own nav/footer) */}
                  <Route path="/" element={<LandingPageAlt />} />
                  <Route path="/customer-landing" element={<CustomerLandingPage />} />
                  <Route path="/barber-landing" element={<BarberLandingPage />} />
                  <Route path="/owner-landing" element={<BarbershopOwnerLandingPage />} />

                  {/* Company & Legal Pages - standalone with branded footer */}
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />

                  {/* Public pages with branded header/footer */}
                  <Route path="/barbershops" element={<BarbershopsPage />} />

                  {/* All other routes use SidebarLayout */}
                  <Route element={<SidebarLayout />}>
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/roles" element={<RoleSelectionPage />} />
                    <Route path="/login" element={<LoginForm />} />
                    <Route path="/register" element={<RegisterForm />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                    <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                    {/* Public group-booking invitation landing page (BOOK-2c) */}
                    <Route path="/group-invitations/:token" element={<GroupInvitationPage />} />
                    {/* Public QR Scan Result Page - handles both scheduled and walk-in customers */}
                    <Route path="/scan" element={<QrScanResultPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <CustomerProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationPreferencesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/sms"
                    element={
                      <ProtectedRoute>
                        <SmsOptInPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute>
                        <MessagesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barbershop/:id"
                    element={<BarbershopDetailPage />}
                  />
                  <Route
                    path="/barbershops/:id"
                    element={<BarbershopDetailPage />}
                  />
                  <Route
                    path="/book"
                    element={
                      <ProtectedRoute>
                        <BookBarberPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking"
                    element={
                      <ProtectedRoute>
                        <BookingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookings"
                    element={
                      <ProtectedRoute>
                        <BookingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookings/confirm"
                    element={
                      <ProtectedRoute>
                        <BookingsRequiringConfirmation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/waitlist"
                    element={
                      <ProtectedRoute>
                        <WaitlistPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/recurring-appointments"
                    element={
                      <ProtectedRoute>
                        <RecurringAppointmentsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/favorites"
                    element={
                      <ProtectedRoute>
                        <FavoriteBarbersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gift-cards"
                    element={
                      <ProtectedRoute>
                        <GiftCardsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-reviews"
                    element={
                      <ProtectedRoute>
                        <MyReviewsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/referrals"
                    element={
                      <ProtectedRoute>
                        <ReferralPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/visual-history"
                    element={
                      <ProtectedRoute>
                        <VisualHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/group-bookings"
                    element={
                      <ProtectedRoute>
                        <GroupBookingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/group-bookings/:id"
                    element={
                      <ProtectedRoute>
                        <GroupBookingDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Advanced Booking Routes */}
                  <Route
                    path="/booking/multi-service"
                    element={
                      <ProtectedRoute>
                        <MultiServiceBookingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking/modify/:id"
                    element={
                      <ProtectedRoute>
                        <BookingModificationPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* BOOK-3c: legacy enhanced-waitlist page retired (its API routes 404) */}
                  <Route path="/booking/waitlist" element={<Navigate to="/waitlist" replace />} />
                  <Route
                    path="/booking/group"
                    element={
                      <ProtectedRoute>
                        <EnhancedGroupBookingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking/recurring"
                    element={
                      <ProtectedRoute>
                        <EnhancedRecurringBookingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/booking-history"
                    element={
                      <ProtectedRoute>
                        <BookingHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/billing-history"
                    element={
                      <ProtectedRoute>
                        <BillingHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription"
                    element={<SubscriptionPage />}
                  />
                  <Route
                    path="/subscription/success"
                    element={
                      <ProtectedRoute>
                        <SubscriptionSuccessPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Customer Membership Routes */}
                  <Route
                    path="/memberships"
                    element={<CustomerMembershipPlansPage />}
                  />
                  <Route
                    path="/memberships/subscribe"
                    element={
                      <ProtectedRoute roles={['Customer']}>
                        <CustomerMembershipSubscribePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/memberships/subscribe/:planId"
                    element={
                      <ProtectedRoute roles={['Customer']}>
                        <BarberMembershipCheckoutPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/memberships/dashboard"
                    element={
                      <ProtectedRoute roles={['Customer']}>
                        <CustomerMembershipDashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Shop Owner Analytics Routes (not part of admin layout) */}
                  <Route
                    path="/analytics/barbers"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarberPerformanceDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics/bookings"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BookingAnalyticsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics/retention"
                    element={
                      <ProtectedRoute roles={["Admin", "ShopOwner"]}>
                        <EnhancedCustomerRetentionDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/support"
                    element={
                      <ProtectedRoute>
                        <SupportTicketPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* UI Components Test Page (Development) */}
                  <Route
                    path="/ui-test"
                    element={<UIComponentsTest />}
                  />

                  {/* Shop Owner Routes */}
                  <Route
                    path="/shop-owner/onboarding"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <ShopOwnerOnboardingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/subscription"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <ShopOwnerSubscriptionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/invitations"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarberInvitationPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/barbershops"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarbershopManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/revenue-forecast"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <RevenueForecastPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/inventory"
                    element={
                      <InventoryProtectedRoute>
                        <InventoryManagementPage />
                      </InventoryProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/check-in-qr"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <CheckInQrCodePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/check-in-qr"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <CheckInQrCodePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/inventory"
                    element={
                      <InventoryProtectedRoute>
                        <InventoryManagementPage />
                      </InventoryProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/barbershops/new"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarbershopFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/barbershops/:id"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarbershopFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/shop-owner/barbershops/:id/barbers"
                    element={
                      <ProtectedRoute requiredRole="ShopOwner">
                        <BarberManagementPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Barber Routes */}
                  <Route
                    path="/barber/dashboard"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/onboarding"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberOnboardingWizard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/subscription"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberSubscriptionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/membership-plans"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberMembershipPlansPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/membership-plans-manage"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberMembershipPlansManagePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription/checkout/success"
                    element={
                      <ProtectedRoute>
                        <SubscriptionCheckoutSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscription/checkout/cancel"
                    element={
                      <ProtectedRoute>
                        <SubscriptionCheckoutCancel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/profile"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/analytics"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/payouts"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberPayoutsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/clients"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberClientsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/calendar"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <CalendarAvailabilityPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/barbershops"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberShopsManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/services"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BarberServicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/clients/at-risk"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <AtRiskClientsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/clients/lost"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <LostClientsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/issued-offers"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <IssuedOffersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/pending-bookings"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <PendingBookingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/customer-settings"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <CustomerTrustSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/loyalty-program"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <BarberLoyaltyProgramPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/promotions"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <BarberPromotionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/campaigns"
                    element={
                      <ProtectedRoute roles={["ShopOwner", "Barber"]}>
                        <BarberCampaignsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/barber/broadcasts"
                    element={
                      <ProtectedRoute requiredRole="Barber">
                        <BroadcastPage />
                      </ProtectedRoute>
                    }
                  />

                    <Route
                      path="/unauthorized"
                      element={
                        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-gray-900 min-h-screen">
                          <div className="px-4 py-6 sm:px-0">
                            <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">Unauthorized</h1>
                            <p className="mt-2 text-gray-600 dark:text-gray-300">You don't have permission to access this page.</p>
                          </div>
                        </div>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-gray-900 min-h-screen">
                          <div className="px-4 py-6 sm:px-0">
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">404 - Page Not Found</h1>
                            <p className="mt-2 text-gray-600 dark:text-gray-300">The page you're looking for doesn't exist.</p>
                          </div>
                        </div>
                      }
                    />
                  </Route>
                </Routes>
              </Suspense>
                </SidebarProvider>
            </Router>
            </BookingProvider>
          </SubscriptionProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
